// noinspection SqlDialectInspection

// Barrel for the app's strings — and the runtime locale switch.
//
// The content lives in the sibling part modules; this file re-exports all of it
// so every existing `popdock-shared/strings/strings-en-us` import keeps working
// unchanged (~700 sites across 9 repos). The module name is historical: it is
// now the locale-aware entry point, not "the en-US file".
//
// Labels/Messages/FieldTypes are Proxies whose property reads resolve against the
// active locale (see ./locale.js). In en-US that is a strict no-op — the override
// table is null and every read returns the part module's own value, guarded by
// tests/strings-noop.test.js. Those three are the ONLY blocks the en-GB delta can
// change; the rest are identical across locales and pass through untouched, which
// keeps the Proxy's blast radius as small as possible.
//
// To add a key: edit labels.js or messages.js. The filename tells you which block
// a value belongs to, so search for the literal in the part file.
//
// ⚠ Reading a string here at MODULE scope freezes it to whichever locale was
// active at import time. Module-scope option/column tables must therefore defer
// the read — use a getter (`{ get Label() { return Labels.X; } }`) or a factory
// function. See tests/strings-locale-switch.test.js.

import { Labels as enLabels } from "./labels.js";
import { Messages as enMessages } from "./messages.js";
import { FieldTypes as enFieldTypes } from "./enums.js";
import { getOverride } from "./locale.js";

// Non-overridable blocks pass straight through.
export * from "./geo.js";
export * from "./mfa.js";
export {
    ActionDisplayTypes,
    ActionTypes,
    ApiRequestMethods,
    ChartTypes,
    CustomEntityTypes,
    DataLakeChunkGranularity,
    DataLakeFileFormats,
    DateFormats,
    DateSummaryMethods,
    DateValues,
    DaysOfWeek,
    DimensionValueTypes,
    FirewallTypes,
    Formats,
    GenerateFiscalPeriodMethods,
    JobEmailAttachmentFormats,
    JoinMethods,
    Months,
    Operators,
    ReportingTreeDateLevels,
    ReportingTreeTypes,
    SummaryMethods,
    TextFormats,
    TimespanUnits,
    WidgetParameterTypes,
} from "./enums.js";

// Resolve one value for the active locale:
//   string -> the override if the locale has one, else the en-US value
//   array  -> a new array with each element resolved
//   object -> a nested accessor carrying the extended key path
//   other  -> returned as-is (functions like Labels.TrialExpiredDate, numbers)
//
// All three Proxied blocks are currently FLAT, so the object branch never fires in
// practice. That matters: a nested accessor would allocate a fresh Proxy per read,
// making identity unstable (X.Y !== X.Y) and breaking any useMemo dependency or
// React key that holds one. tests/strings-noop.test.js pins string-leaf identity so
// this stays true if a nested block is ever added here.
function resolve(block, key, value) {
    if (typeof value === "string") {
        const override = getOverride(block, key);
        return override === undefined ? value : override;
    }
    if (Array.isArray(value)) {
        return value.map((item) => resolve(block, key, item));
    }
    if (value !== null && typeof value === "object") {
        return makeAccessor(block, value, `${key}.`);
    }
    return value;
}

// The target is the real source object, so Object.keys, spread, JSON.stringify,
// `in`, enumerability and symbol access all behave exactly as before. A missing key
// falls through to Reflect.get and yields undefined — no key-echo leaking into the
// UI, matching the pre-Proxy behaviour.
function makeAccessor(block, target, prefix = "") {
    return new Proxy(target, {
        get(t, key, receiver) {
            if (typeof key === "string" && Object.prototype.hasOwnProperty.call(t, key)) {
                return resolve(block, `${prefix}${key}`, t[key]);
            }
            return Reflect.get(t, key, receiver);
        },
    });
}

export const Labels = makeAccessor("Labels", enLabels);
export const Messages = makeAccessor("Messages", enMessages);
export const FieldTypes = makeAccessor("FieldTypes", enFieldTypes);
